import { Moment } from "moment";

import { EVALUATION_METRICS, PracticeSlug } from "models";

export type Field = {
  id: string;
  flik: string;
  name: string;
  number: string;
  reviewed: boolean;
  size: number | null;
  updatedAt: string;
  isInRedzone: boolean;
  location?: Coordinates;
};

export type FieldsListResponse = {
  fields: {
    assignedModuleIds: string[];
    id: string;
    name: string;
    reviewed: boolean | null;
    size: number | null;
    isInRedzone: boolean;
  }[];
  page: number;
  totalItemsCount: number;
  itemsPerPageCount: number;
  totalPageCount: number;
};

export type Coordinates = {
  lat: number;
  lng: number;
};

type MethodCrop = {
  crop: string | number;
  year: number;
};

export type FieldDetails = {
  id: string;
  name: string;
  nearby: string[];
  number: string;
  size: number;
  soilType: string;
  soilPoints: number;
  humusContent: string;
  bulkDensity: number;
  soilDate: string;
  location: Coordinates;
  crops: MethodCrop[];
  flik: string;
  reviewed: boolean;
  exceedsHectareLimit: boolean;
  isInRedzone: boolean;
  regionId: string;
};

type FieldGeometry = {
  center: Coordinates;
  geometry: Coordinates[];
  type: ShapeType;
};

type FieldRedzoneRequest = {
  id: string;
  state: string;
  accurate: boolean;
  confirmed: boolean;
  geometry?: FieldGeometry | null;
};

export type AddFieldRequestBody = {
  name: string;
  nearby?: string[];
  number?: string;
  size: number;
  soilType?: string;
  soilPoints?: number;
  humusContent?: string;
  soilDate?: string;
  geometry: FieldGeometry;
  crops?: MethodCrop[];
  flik?: string;
  location: Coordinates;
  redzone?: FieldRedzoneRequest | null;
  isInRedzone?: boolean;
};

export type FieldRedzone = {
  id: string;
  fieldId: string;
  redzoneId: string;
  year: number;
  confirmed: boolean | null;
  field: {
    id: string;
    number: string;
    name: string;
  };
};

export type RedzoneException = {
  description: string;
  disabled: boolean;
  id: string;
};

export type Redzone = {
  id: string;
  state: string;
  accurate: boolean;
  geometry: string;
  createdAt: string;
  updatedAt: string;
};

export type RedzoneResponse = {
  id: string;
  state: string;
  accurate: boolean;
  geometry: Coordinates[];
};

export type ShapeType = "shapeFile" | "googlePolygon" | "digifarm" | "square";

export type FieldShape = {
  fieldId: string;
  geometry: Coordinates[];
  center: Coordinates;
  type: ShapeType;
  manual?: boolean;
};

export type FieldListField = {
  id: string; // uuid
  isInRedzone: boolean;
  location: Coordinates;
  name: string;
  redzones: {
    confirmed: boolean | null;
    redzoneId: string;
  }[];
  regionId: string;
  size: number;
};

export enum CROP_TYPE_FCPO {
  MAIN = "MAIN",
  COVER = "COVER",
  UNDERSOWING = "UNDERSOWING",
}

export enum PRACTICE_BUNDLE_SLUG {
  CROPS = "crops",
  FERTILIZER = "fertilizer",
  TILLAGE = "tillage",
}

export type PracticeBundle = {
  active: boolean; // must be true if confirmed is true
  hasConfirmedData: boolean;
  slug: (typeof PRACTICE_BUNDLE_SLUG)[keyof typeof PRACTICE_BUNDLE_SLUG];
  startYear: number | null;
  discontinuedYear: number | null;
  enrollable: boolean;
};

export type ActivePracticeBundle = PracticeBundle & { hasMissingData: boolean };

export type PracticeFCPO = {
  id?: string;
  startDate?: string;
  endDate: string;
  type: Extract<PracticeSlug, "mineralFertilizers" | "organicFertilizers" | "tillages">;
  assignedPracticeId: string; // uuid
  isConfirmed: boolean;
  year?: number;
  cropId?: string; // maybe only for tillage
  payout?: number; // also not implementable now
  verificationStatus?: VerificationStatus;
  // start of data just for organic fertilisers
  nValue?: number;
  amount?: number;
  selfProduced?: boolean | null;
  applicationType?: string; // uuid
  tillageDepth?: number; // only relevant for tillage
  seedingPracticeId?: string; // only crops have a seedingPracticeId
};

export const VERIFICATION_STATUS = {
  approved: "approved",
  autoApproved: "auto-approved",
  pending: "pending",
  rejected: "rejected",
} as const;

export type VerificationStatus = (typeof VERIFICATION_STATUS)[keyof typeof VERIFICATION_STATUS] | null;

export type PracticeFCPORequest = {
  startDate?: string;
  endDate: string;
  assignedPracticeId: string; // uuid
  type: Extract<PracticeSlug, "mineralFertilizers" | "organicFertilizers" | "tillages">;
  // start of data just for fertilizers
  amount?: number;
  // start of data just for organic fertilizers
  nValue?: number;
  selfProduced?: boolean | null;
  applicationType?: string; // uuid
  tillageDepth?: number; // only relevant for tillage
  verificationStatus?: VerificationStatus;
};

export type CropFCPO = CropFCPORequest & {
  id: string;
  cropName: string;
  cropId: string; // uuid
  cropType: CROP_TYPE_FCPO;
  harvestStartDate: string | null;
  harvestEndDate: string;
  seedingStartDate: string;
  seedingEndDate: string | null;
  seedingPracticeType: string | null; // uuid
  seedingVerificationStatus?: VerificationStatus; // Seeding verification status is now part of crop
  seedingPracticeId?: string; // Seeding verification status is now part of crop
  hasResidue: boolean | null;
  isConfirmed: boolean | null;
  payout: number; // To be implemented later, once CCC finishes the footprint service
  translationSlug: string;
  year?: number;
  yieldTonsPerHa: number | null;
  verificationStatus?: VerificationStatus;
  updatedAt?: string;
};

export type CropFCPORequest = {
  cropId: string;
  cropType: CROP_TYPE_FCPO;
  harvestStartDate?: string | null;
  harvestEndDate: string;
  seedingStartDate: string;
  seedingEndDate?: string | null;
  seedingPracticeType?: string | null; // uuid
  seedingVerificationStatus: VerificationStatus;
  hasResidue: boolean | null;
  cropMixId?: string;
  yieldTonsPerHa?: number | null;
  mandatory?: boolean | null;
  redzoneExceptionId?: string | null;
};

export type FertilizerManagement = { implements4R: boolean; year: number; fieldCropId: string };

export type FieldPracticeResponse = {
  availablePracticeBundles: PracticeBundle[]; // saved on a per-field level
  crops: CropFCPO[];
  fieldHasFailedVerification: boolean;
  practices: PracticeFCPO[];
  fertilizerManagements: FertilizerManagement[];
  fieldId: string;
};

export type FieldCropRecommendation = {
  cropId: string;
  translationSlug: string;
  hasResidue: boolean;
  payoutDifference: {
    withResidue: number | null;
    withoutResidue: number;
  };
};

type FieldCropRecommendationGroup = {
  seedingDate: string | null;
  harvestDate: string | null;
  recommendations: FieldCropRecommendation[];
};

export type CategorizedFieldCropRecommendation = {
  year: number;
  catchCrop: FieldCropRecommendationGroup;
  undersowing: FieldCropRecommendationGroup;
};

export type FieldCropRecommendationsResponse = { [fieldId: string]: CategorizedFieldCropRecommendation[] };
export type FieldCropRecommendationsSimulationsResponse = { [simulationId: string]: CategorizedFieldCropRecommendation[] };

export type BasicFieldDetails = {
  id: string;
  name: string;
  size: number;
  location: {
    lat: number;
    lng: number;
  };
  reviewed: boolean;
  isInRedzone: boolean;
};

export const ZERO_UUID = "00000000-0000-0000-0000-000000000000";

export type FieldSimulation = {
  id: string;
  fieldId: string;
  farmId: string;
  userId: string;
  name: string;
};

export type FieldEvaluationMetricCorrectionItem = {
  id: string;
  year: number;
  value: number;
};

export type FieldEvaluationMetricCorrection = {
  [key in EVALUATION_METRICS]: Array<FieldEvaluationMetricCorrectionItem>;
};

export type FieldEvaluationMetricCorrectionsResponse = {
  [fieldId: string]: FieldEvaluationMetricCorrection;
};

export type FieldSizeUpdate = {
  id: string;
  farmId: string;
  fieldId: string;
  previousSize: number;
  newSize: number;
  updateMethod: string;
  createdAt: Date;
  updatedAt: Date;
};

export type Geometry = FieldShape & { geometry: Coordinates[] };

export type Area = {
  center: Coordinates;
  area: number;
  geometry: Geometry;
};

export type FieldPracticeFilterOptionsResponse = {
  cropIds: string[];
  years: number[];
};

export type ItemWithMissingData = {
  id: string;
  startTime: Moment;
};

export type ItemsWithMissingData = {
  cropsWithMissingPractices: ItemWithMissingData[];
  practicesWithMissingData: ItemWithMissingData[];
  missingCrops: ItemWithMissingData[];
  cropsWithDataToConfirm: ItemWithMissingData[];
  cropsWithVerificationEditingRequests: ItemWithMissingData[];
  coverCropsWithMissingRedzoneException: ItemWithMissingData[];
  hasErrors: boolean;
};

export type ItemsWithMissingDataByFieldId = {
  [fieldId: string]: ItemsWithMissingData;
};

export type EditingRequestPlaceholderYears = { [CROP_TYPE_FCPO.MAIN]: number[]; [CROP_TYPE_FCPO.COVER]: number[]; [CROP_TYPE_FCPO.UNDERSOWING]: number[] };
